import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WishListContext } from "../../context/WishListProvider";
import "./ProductCard.css";
import { CartContext } from "../../context/CartProvider";
import { FaPlus, FaCheck, FaHeart, FaRegHeart, FaMinus } from "react-icons/fa";

const ProductCard = ({ item, categories }) => {
  const { addToCart: addItemToCartContext, cart, increment, decrement, removeFromCart } = useContext(CartContext);
  const { addWishList, removeWishList, getExistingWishlist } = useContext(WishListContext);
  const navigate = useNavigate();

  const isProductAdded = cart?.some(
    (cartItem) => cartItem.product_id === item._id
  );

  const cartItem = cart?.find((cartItem) => cartItem.product_id === item._id);
  const itemQuantity = cartItem?.quantity || 0;

  const isWishlisted = getExistingWishlist(item._id);

  const calculateDiscount = (originalPrice, discountedPrice) => {
    if (!originalPrice || !discountedPrice || Number(originalPrice) === 0 || Number(originalPrice) <= Number(discountedPrice)) {
      return 0;
    }
    return Math.round(
      ((Number(originalPrice) - Number(discountedPrice)) / Number(originalPrice)) * 100
    );
  };

  const getCategoryName = (categoryId) => {
    const category = categories?.find((cat) => cat._id === categoryId);
    return category ? category.catagoryname : "Category";
  };

  const getPriceRange = () => {
    if (item.variation === "Variable" && item.attributes && item.attributes.length > 0) {
      const prices = item.attributes.map(attr => Number(attr.other_attributes.discount_price || attr.other_attributes.price));
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      if (minPrice === maxPrice) {
        return `£${minPrice.toFixed(2)}`;
      }
      return `£${minPrice.toFixed(2)} - £${maxPrice.toFixed(2)}`;
    }
    return null;
  };

  const priceRange = getPriceRange();

  const handleAddToCart = (e) => {
    e.stopPropagation();

    const isVariable = item.variation === "Variable" && item.attributes && item.attributes.length > 0;

    if (isVariable && !isProductAdded) {
      navigate(`/product_details/${item._id}`);
      return;
    }

    const priceToUse = item?.attributes?.[0]?.other_attributes?.discount_price ||
      item.discount_price ||
      item.reguler_price;
    const stockAvailable = item?.attributes?.[0]?.other_attributes?.stock ?? item.stock;

    if (stockAvailable === 0 && !isProductAdded) {
      return;
    }

    const productToAdd = {
      product_id: item._id,
      product_name: item.product_name,
      price: priceToUse,
      productImage: item.productImage,
      stock: stockAvailable,
      reguler_price: item.reguler_price,
      discount_price: item.discount_price,
      selectedAttributes: item?.attributes?.[0] || {},
    };

    addItemToCartContext(productToAdd);
  };

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    if (isWishlisted) {
      removeWishList(item._id);
    } else {
      addWishList({
        product_id: item._id,
        product_name: item.product_name,
        price: item.variation === "Variable" && item.attributes?.[0]
          ? (item.attributes[0].other_attributes.discount_price || item.attributes[0].other_attributes.price)
          : (item.discount_price || item.reguler_price),
        productImage: item.productImage,
      });
    }
  };

  const totalQuantity = cart
    ?.filter((cartItem) => cartItem.product_id === item._id)
    .reduce((total, ci) => total + ci.quantity, 0) || 0;

  const handleIncrement = (e) => {
    e.stopPropagation();
    if (item.variation === "Variable") {
      navigate("/cart");
      return;
    }
    increment(cartItem?.cartItemId || item._id);
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    if (item.variation === "Variable") {
      navigate("/cart");
      return;
    }
    if (totalQuantity <= 1) {
      removeFromCart(cartItem?.cartItemId || item._id);
    } else {
      decrement(cartItem?.cartItemId || item._id);
    }
  };

  const handleCardClick = () => {
    navigate(`/product_details/${item._id}`);
  };

  const discountPercentage = calculateDiscount(item.reguler_price, item.discount_price);
  const categoryName = getCategoryName(item.category);
  const currentStock = item.variation === "Variable" && item.attributes
    ? item.attributes.reduce((acc, curr) => acc + (Number(curr.other_attributes.stock) || 0), 0)
    : item.stock;

  return (
    <div className="product-card-professional" onClick={handleCardClick}>
      {/* Image Section */}
      <div className="pro-card-image-area">
        <button
          className={`pro-wishlist-btn ${isWishlisted ? 'active' : ''}`}
          onClick={handleWishlistToggle}
          title={isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
        >
          {isWishlisted ? <FaHeart /> : <FaRegHeart />}
        </button>

        {discountPercentage > 0 && !priceRange && (
          <div className="pro-discount-badge">
            <span className="badge-text">{discountPercentage}% <br /> OFF</span>
          </div>
        )}
        {item.variation === "Variable" && (
          <div className="pro-variant-badge" style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            backgroundColor: '#2c3e50',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            zIndex: 2
          }}>
            <span className="badge-text">VARIANTS</span>
          </div>
        )}
        <img
          src={item.productImage || "https://placehold.co/200x200/e2e8f0/a0aec0?text=No+Image"}
          className="pro-product-image"
          alt={item.product_name || "Product"}
          onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/200x200/e2e8f0/a0aec0?text=No+Image"; }}
        />
      </div>

      {/* Details Section */}
      <div className="pro-card-details">
        <h3 className="pro-product-title text-decoration-none">{item.product_name || "Product Name"}</h3>
        <p className="pro-product-unit">{categoryName} {currentStock > 0 ? `(${currentStock} in stock)` : '(Out of stock)'}</p>

        <div className={`pro-card-footer ${isProductAdded ? 'in-cart' : ''}`}>
          <div className="pro-price-container">
            {priceRange ? (
              <span className="pro-current-price">{priceRange}</span>
            ) : (
              <>
                {item.discount_price && Number(item.discount_price) < Number(item.reguler_price) ? (
                  <span className="pro-current-price">£{item.discount_price}</span>
                ) : (
                  <span className="pro-current-price">£{item.reguler_price || "0.00"}</span>
                )}
              </>
            )}
          </div>

          {isProductAdded ? (
            <div className="pro-quantity-controls">
              <button
                onClick={handleDecrement}
                className="pro-quantity-btn pro-decrement-btn"
                title="Decrease quantity or remove from cart"
              >
                <FaMinus />
              </button>
              <span className="pro-quantity-display">{totalQuantity}</span>
              <button
                onClick={handleIncrement}
                className="pro-quantity-btn pro-increment-btn"
                disabled={totalQuantity >= 15}
                title="Increase quantity"
              >
                <FaPlus />
              </button>
            </div>
          ) : (
            <button
              onClick={handleAddToCart}
              disabled={currentStock === 0 && !isProductAdded}
              className={`pro-add-btn ${isProductAdded ? 'added' : ''} ${currentStock === 0 && !isProductAdded ? 'disabled' : ''}`}
              title={isProductAdded ? "Added to Cart" : (item.variation === "Variable" ? "Select Options" : "Add to Cart")}
            >
              {isProductAdded ? <FaCheck /> : <FaPlus className="red-plus" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
